import React, { useMemo, useState } from 'react';
import { validateSigePlan } from '../../services/geminiService';
import { Arbeitsschritt, Gefaehrdung, Schutzmassnahme, SigeBlock, SigeProject } from './types';
import { PlanVorschau } from './PlanVorschau';
import { kiAntwortAlsObjekt } from './uebernahme';
import { pruefeSigePlanVollstaendigkeit } from './vollstaendigkeit';
import type { SigeHinweis } from './planungAnbindung';
import { PlanungHinweise } from './PlanungHinweise';
import { Button } from '../Button';
import { AlertTriangle, Bot, CheckCircle2, Eye, Info, Loader2, XCircle } from 'lucide-react';

// Schritt 6 des Anlage-Assistenten: harte Regeln (lokal, ohne Netz), optionale
// KI-Prüfung und PDF-Vorschau. Das Abschließen selbst löst der Assistent aus.
// Die bejahten Anhang-II-Nummern der Planungsphase fließen als Hinweisregel
// in die Vollständigkeit ein (warnt, blockiert nicht).

interface Props {
  plan: SigeProject;
  schritte: Arbeitsschritt[];
  bloecke: SigeBlock[];
  gefaehrdungen: Record<string, Gefaehrdung[]>;
  massnahmen: Record<string, Schutzmassnahme[]>;
  /** Zutreffende Anhang-II-Nummern nach der Planungsphase; leer ohne Planungsphase. */
  anhang2Nummern: readonly number[];
  hinweise: readonly SigeHinweis[];
  onHinweisBeruecksichtigt: (hinweis: SigeHinweis) => Promise<void>;
  melde: (text: string, ton?: 'info' | 'fehler') => void;
}

interface KiBefund {
  titel?: string;
  schweregrad?: string;
  empfehlung?: string;
  schritt?: number;
}

const SCHWERE_STIL: Record<string, { box: string; icon: React.ReactNode; label: string }> = {
  fehler: { box: 'bg-red-50 border-red-200 text-red-800', icon: <XCircle className="w-4 h-4 shrink-0 mt-0.5" />, label: 'Fehler' },
  warnung: { box: 'bg-amber-50 border-amber-200 text-amber-800', icon: <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />, label: 'Warnung' },
  info: { box: 'bg-blue-50 border-blue-200 text-blue-800', icon: <Info className="w-4 h-4 shrink-0 mt-0.5" />, label: 'Hinweis' },
};

export const WizardStepPruefung: React.FC<Props> = ({
  plan, schritte, bloecke, gefaehrdungen, massnahmen, anhang2Nummern, hinweise, onHinweisBeruecksichtigt, melde,
}) => {
  const [kiLaeuft, setKiLaeuft] = useState(false);
  const [kiBefunde, setKiBefunde] = useState<KiBefund[] | null>(null);
  const [kiZusammenfassung, setKiZusammenfassung] = useState<string>('');
  const [vorschauOffen, setVorschauOffen] = useState(false);

  const pruefung = useMemo(
    () => pruefeSigePlanVollstaendigkeit({ plan, arbeitsschritte: schritte, bloecke, anhang2Nummern }),
    [plan, schritte, bloecke, anhang2Nummern],
  );

  const kiPruefen = async () => {
    setKiLaeuft(true);
    try {
      const antwort = await validateSigePlan({
        plan_nr: plan.plan_nr,
        baumassnahme: plan.baumassnahme_name,
        bauzeit: { von: plan.zeitraum_von, bis: plan.zeitraum_bis },
        bloecke: bloecke.map(b => b.typ),
        arbeitsschritte: schritte.map(s => ({
          bezeichnung: s.bezeichnung,
          gewerk: s.gewerk,
          hauptabschnitt: s.hauptabschnitt,
          zeitraum: [s.zeitraum_von, s.zeitraum_bis],
          schutzmassnahmen: s.schutzmassnahmen || [],
          vorschriften: s.vorschriften || [],
        })),
      });
      const daten = kiAntwortAlsObjekt(antwort);
      setKiBefunde(Array.isArray(daten?.befunde) ? daten.befunde : []);
      setKiZusammenfassung(String(daten?.zusammenfassung || ''));
    } catch (e: any) {
      console.error('KI-Prüfung fehlgeschlagen', e);
      melde(`KI-Prüfung fehlgeschlagen: ${e?.message || String(e)}`, 'fehler');
    } finally {
      setKiLaeuft(false);
    }
  };

  const gruppiert = useMemo(() => {
    const gruppen: Record<string, KiBefund[]> = { fehler: [], warnung: [], info: [] };
    (kiBefunde || []).forEach(b => {
      const key = (b.schweregrad || 'info').toLowerCase();
      (gruppen[key] || gruppen.info).push(b);
    });
    return gruppen;
  }, [kiBefunde]);

  const farbe = pruefung.score >= 90 ? 'text-emerald-600' : pruefung.score >= 60 ? 'text-amber-600' : 'text-red-600';
  const balken = pruefung.score >= 90 ? 'bg-emerald-500' : pruefung.score >= 60 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-4">
          <div className={`text-3xl font-black ${farbe}`}>{pruefung.score}<span className="text-base font-bold text-slate-400">/100</span></div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">Vollständigkeit</div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1.5">
              <div className={`h-full ${balken} transition-all`} style={{ width: `${pruefung.score}%` }} />
            </div>
          </div>
          <Button variant="secondary" onClick={() => setVorschauOffen(true)} className="text-sm shrink-0">
            <Eye className="w-4 h-4 mr-2" /> PDF-Vorschau
          </Button>
        </div>

        <div className="mt-4 space-y-1.5">
          {pruefung.fehlend.every(f => f.hinweis) ? (
            <div className="text-sm text-emerald-700 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Alle Pflichtangaben liegen vor.
            </div>
          ) : (
            pruefung.fehlend.filter(f => !f.hinweis).map(f => (
              <div
                key={f.id}
                className={`text-xs border rounded-lg px-2.5 py-2 flex items-start gap-2 ${
                  f.blockierend ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}
              >
                {f.blockierend ? <XCircle className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
                <span>{f.text}</span>
              </div>
            ))
          )}
          {pruefung.fehlend.filter(f => f.hinweis).map(f => (
            <div key={f.id} className={`text-xs border rounded-lg px-2.5 py-2 flex items-start gap-2 ${SCHWERE_STIL.info.box}`}>
              {SCHWERE_STIL.info.icon}
              <span>{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      <PlanungHinweise hinweise={hinweise} onBeruecksichtigt={onHinweisBeruecksichtigt} />

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-800">KI-Prüfung (optional)</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Prüft den Plan gegen Best-Practice und meldet fehlende oder unplausible Punkte. Ersetzt
              die fachliche Prüfung durch den SiGeKo nicht.
            </div>
          </div>
          <Button variant="ai" onClick={kiPruefen} disabled={kiLaeuft} className="text-sm shrink-0">
            {kiLaeuft ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
            {kiBefunde ? 'Erneut prüfen' : 'KI-Prüfung starten'}
          </Button>
        </div>

        {kiZusammenfassung && <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{kiZusammenfassung}</div>}

        {kiBefunde && kiBefunde.length === 0 && (
          <div className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Keine Befunde.</div>
        )}

        {(['fehler', 'warnung', 'info'] as const).map(grad => (
          gruppiert[grad].length > 0 && (
            <div key={grad} className="space-y-1.5">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                {SCHWERE_STIL[grad].label} ({gruppiert[grad].length})
              </div>
              {gruppiert[grad].map((b, i) => (
                <div key={i} className={`text-xs border rounded-lg px-2.5 py-2 flex items-start gap-2 ${SCHWERE_STIL[grad].box}`}>
                  {SCHWERE_STIL[grad].icon}
                  <span>
                    <strong>{b.titel || 'Befund'}</strong>
                    {b.empfehlung ? <>: {b.empfehlung}</> : null}
                  </span>
                </div>
              ))}
            </div>
          )
        ))}
      </div>

      {vorschauOffen && (
        <PlanVorschau
          sigeProject={plan}
          arbeitsschritte={schritte}
          gefaehrdungen={gefaehrdungen}
          massnahmen={massnahmen}
          bloecke={bloecke}
          onDownload={() => melde('Der Download läuft über „Vorschau & Export" im Editor; dort wird die Leistung abgerechnet.')}
          onClose={() => setVorschauOffen(false)}
        />
      )}
    </div>
  );
};
